import { LinkText } from '@/components/ui/link-text';
import { Text } from '@/components/ui/text';
import { useLocale } from '@/lib/hooks/use-locale';
import type { CrmEventDetails } from '@/server/types/infer-trpc-types';
import { getRecurrenceWeeksCount, parseRecurrenceString } from '@/utils/rrule';
import { formatISOTimeForDisplay } from '@/utils/time-utils';
import { intlFormat, parseISO, setDay } from 'date-fns';
import { MapPinIcon, StoreIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface EventViewProps {
    eventDetails: CrmEventDetails;
}

const getGoogleMapsUrl = (address: string) => {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
};

export const EventDetailsSchedule = ({ eventDetails }: EventViewProps) => {
    const { t, locale } = useLocale('events');
    const navigate = useNavigate();

    const dateFormatted = eventDetails.date
        ? intlFormat(
              parseISO(eventDetails.date),
              {
                  weekday: 'long',
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
              },
              { locale },
          )
        : '';

    const timeFormatted =
        eventDetails.start_time && eventDetails.end_time
            ? t('card_time_format', {
                  startTime: formatISOTimeForDisplay(eventDetails.start_time),
                  endTime: formatISOTimeForDisplay(eventDetails.end_time),
              })
            : eventDetails.start_time
              ? formatISOTimeForDisplay(eventDetails.start_time)
              : '';

    function getRecurrenceText() {
        if (!eventDetails.recurrence) return undefined;

        const recurrence = parseRecurrenceString(eventDetails.recurrence, eventDetails.startDate);

        if (!recurrence) return;

        const { interval, weekdays } = recurrence;

        if (!interval || !weekdays?.length) return;

        return (
            <Text color="neutral-950" size="sm">
                {t('events__schedule__recurrence', {
                    count: interval,
                    weekdays: weekdays
                        .sort()
                        .map((weekday) =>
                            new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(
                                setDay(new Date(), weekday),
                            ),
                        )
                        .join(', '),
                })}
            </Text>
        );
    }

    return (
        <div className="flex flex-col rounded-lg border-b border-neutral-200 bg-white gap-5 py-4 px-3">
            {/* Description / Title */}
            <div className="flex flex-col gap-3">
                <Text weight="semibold" color="neutral-950" size="base">
                    {eventDetails.description || t('description_empty')}
                </Text>

                {/* Date + Time + Recurrence */}
                <div className="flex flex-col gap-2">
                    <div className="flex flex-col sm:flex-row">
                        <Text color="neutral-950" size="sm">
                            {dateFormatted}
                        </Text>
                        {!!timeFormatted && (
                            <>
                                <Text color="neutral-950" size="sm" className="hidden sm:inline">
                                    &nbsp;•&nbsp;
                                </Text>
                                <Text color="neutral-950" size="sm">
                                    {timeFormatted}
                                </Text>
                            </>
                        )}
                    </div>

                    {getRecurrenceText()}
                </div>
            </div>

            {/* Customer + Location */}
            <div className="flex flex-col gap-3">
                {/* Customer */}
                {eventDetails.customer && (
                    <div className="flex items-start gap-2">
                        <StoreIcon className="mt-px size-3.5 shrink-0 text-neutral-600" />
                        <div className="flex flex-col gap-1">
                            <Text color="neutral-600" size="sm" weight="medium">
                                {t('events__customer_label')}
                            </Text>
                            <LinkText
                                href={`/customers/${eventDetails.customer.customerId}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate(`/customers/${eventDetails.customer.customerId}`);
                                }}
                            >
                                {eventDetails.customer.customerName}
                            </LinkText>
                        </div>
                    </div>
                )}

                {/* Location */}
                {eventDetails.address && (
                    <div className="flex items-start gap-2">
                        <MapPinIcon className="mt-px size-3.5 shrink-0 text-neutral-600" />
                        <div className="flex flex-col gap-1">
                            <Text color="neutral-600" size="sm" weight="medium">
                                {t('events__location_label')}
                            </Text>
                            <LinkText
                                href={getGoogleMapsUrl(eventDetails.address)}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {eventDetails.address}
                            </LinkText>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
